import React, { useContext, useState } from 'react';
import { Url } from '@wix/da-url';
import { useTranslation } from 'react-i18next';
import { PapiUser } from '@wix/da-papi-types';
import { useFlag } from '@wix/da-react-context/pkg/flags/hooks/useFlag';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import LoadingIndicator from '@wix/da-shared-react/pkg/LoadingIndicator';
import MessageModal from '@wix/da-ds/pkg/modals/dsPresets/MessageModal';
import ThemedImg from '@wix/da-ds/pkg/ThemedImg';
import Zone, { ZoneGroup } from '../../Layout/Zone';
import { ZoneType } from '../../../../types/layout';
import { ModuleType } from '../../../../types/modules';
import TiersBox from '../../UserWidgets/Tiers/ProductBox';
import SubscriptionCenterBox from '../../UserWidgets/SubscriptionCenter';
import SubscriptionTipsBox from '../../UserWidgets/SubscriptionTips';
import TierContent from '../../UserWidgets/TierContent';
import SubscriptionsCoreOnlyBlock from './SubscriptionsCoreOnlyBlock';
import SubscriptionsVisitorEmptyState from './SubscriptionsVisitorEmptyState/SubscriptionsVisitorEmptyState';
import s from './SubscriptionsSection.scss';

export interface Props {
  isSectionLoading: boolean;
  isOwner: boolean;
  hasTiers: boolean;
  profileOwnerUser: PapiUser;
}

const SubscriptionsSection: React.FC<Props> = ({
  isOwner,
  hasTiers,
  profileOwnerUser,
  isSectionLoading,
}) => {
  const isMobile = useContext(MobileContext);
  const { t } = useTranslation();
  let initShowSuccessModal = false;
  if (typeof window !== 'undefined') {
    // eslint-disable-next-line @wix/da/window-object-typeof-check
    const currentUrl = new URL(window.location.href);
    initShowSuccessModal = !!currentUrl.searchParams.get(
      'tier-publish-success'
    );
  }
  const [showSuccessModal, setShowSuccessModal] =
    useState(initShowSuccessModal);
  const updatedTiers = useFlag('updated_tier_cards');
  if (isSectionLoading) {
    return <LoadingIndicator />;
  }
  if (!hasTiers) {
    if (isOwner) {
      return <SubscriptionsCoreOnlyBlock className={s['core-only-block']} />;
    } else {
      return (
        <SubscriptionsVisitorEmptyState profileOwnerUser={profileOwnerUser} />
      );
    }
  }

  return (
    <div className={s['root']}>
      {updatedTiers && showSuccessModal && (
        <MessageModal
          variant="vertical"
          title={t('page.subscriptions.publishedSuccessPopup.title')}
          message={t('page.subscriptions.publishedSuccessPopup.message')}
          image={
            <ThemedImg
              srcDark={Url.staticCDNLink(
                'eclipse/member-subscriptions/publish-success-dark.svg'
              )}
              srcLight={Url.staticCDNLink(
                'eclipse/member-subscriptions/publish-success-light.svg'
              )}
              width={217}
              height={259}
            />
          }
          confirmButtonLabel={t(
            'page.subscriptions.publishedSuccessPopup.button.label'
          )}
          onConfirm={() => setShowSuccessModal(false)}
        />
      )}
      <ZoneGroup>
        {isOwner && (
          <Zone
            className={s['top-zone']}
            locked={true}
            type={ZoneType.MID_RIGHT}
            mapModuleTypeToWidgetComponent={
              isMobile
                ? {
                    [ModuleType.SUBSCRIPTION_CENTER]: SubscriptionCenterBox,
                  }
                : {
                    [ModuleType.SUBSCRIPTION_CENTER]: SubscriptionCenterBox,
                    [ModuleType.SUBSCRIPTION_TIPS]: SubscriptionTipsBox,
                  }
            }
          />
        )}
        <Zone
          locked={true}
          type={ZoneType.MID_RIGHT}
          mapModuleTypeToWidgetComponent={{
            [ModuleType.TIERS]: TiersBox,
            [ModuleType.TIER_CONTENT]: TierContent,
          }}
        />
      </ZoneGroup>
    </div>
  );
};
SubscriptionsSection.displayName = 'SubscriptionsSection';

export default SubscriptionsSection;
